import axios from "axios";
import { useNavigate } from "react-router-dom";
import { deleteRequest, postRequest, getRequest } from "utils/request";
import { useDispatch, useSelector } from "react-redux";
import React, { useState, useEffect, useRef } from "react";
// mui
import {
  AppBar,
  Toolbar,
  useMediaQuery,
  Box,
  Typography,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Badge,
  Paper,
  alpha,
  Tooltip,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
// components
// utils
// config
// enums
import { EntityStatus, UserRole } from "enums/account";
import { EntityMenuProps } from "./types/header";
import { Entity } from "types/main";
import { MediaFolders } from "enums/request";
// assets
import styles from "./assets/scss/header.module.scss";
import logo from "assets/imgs/logo.png";
import logoWithLine from "assets/imgs//logo-line.png";
import NotificationsIcon from "./assets/imgs/alarm.svg";
import NotificationIcon from "./assets/imgs/alarm-green.svg";
import NotificationIconRead from "assets/imgs/mdi_tick-circle-outline.svg";
// mui icons
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SwitchAccountIcon from "@mui/icons-material/SwitchAccount";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
// data
import { appActions, setAppData } from "store/slices/app";
import { uiActions } from "store/ui/ui-slice";

interface HeaderProps {}

interface EntityMenuPropsWithDispatch extends EntityMenuProps {}

const EntityMenu: React.FC<EntityMenuPropsWithDispatch> = ({ isOpen }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const app = useSelector((state: any) => state.app?.allData || {});

  if (!isOpen) return null;

  const selectedEntity = app.entities_info.find(
    (entity: any) => entity.role === UserRole.OWNER,
  );

  // Safe color getter with fallbacks
  const getColor = (path: string, fallback: string) => {
    const paths = path.split(".");
    let value: any = theme;
    for (const p of paths) {
      value = value?.[p];
      if (!value) return fallback;
    }
    return value || fallback;
  };

  const primaryColor = getColor("palette.primary.main", "#7E57C2");
  const warningColor = getColor("palette.warning.main", "#FFB74D");
  const successColor = getColor("palette.success.main", "#81C784");
  const textPrimary = getColor("palette.text.primary", "#2D3748");
  const textSecondary = getColor("palette.text.secondary", "#718096");
  const dividerColor = getColor("palette.divider", "#E2E8F0");
  const colorPalette = [
    "#7E57C2",
    "#5C6BC0",
    "#42A5F5",
    "#26C6DA",
    "#66BB6A",
    "#9CCC65",
    "#D4E157",
    "#FFCA28",
    "#FFA726",
    "#FF7043",
  ];

  return (
    <Paper
      elevation={8}
      sx={{
        position: "absolute",
        top: "60px",
        right: "20px",
        backgroundColor: "white",
        borderRadius: "16px",
        overflow: "hidden",
        border: `1px solid ${alpha("#E2E8F0", 0.3)}`,
        boxShadow:
          "0 20px 60px rgba(0, 0, 0, 0.15), 0 5px 15px rgba(0, 0, 0, 0.08)",
        zIndex: 1500,
        minWidth: "320px",
        maxWidth: "380px",
        overflowY: "auto",
        maxHeight: "480px",
        animation: "slideDown 0.3s ease-out",
        "@keyframes slideDown": {
          "0%": {
            opacity: 0,
            transform: "translateY(-20px) scale(0.95)",
          },
          "100%": {
            opacity: 1,
            transform: "translateY(0) scale(1)",
          },
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: "-10px",
          right: "28px",
          width: "20px",
          height: "20px",
          backgroundColor: "white",
          transform: "rotate(45deg)",
          borderLeft: `1px solid ${alpha("#E2E8F0", 0.3)}`,
          borderTop: `1px solid ${alpha("#E2E8F0", 0.3)}`,
          boxShadow: "-2px -2px 5px rgba(0, 0, 0, 0.05)",
        },
      }}
    >
      {/* Menu Header */}
      <Box
        sx={{
          padding: "20px 24px 16px",
          background: `linear-gradient(135deg, ${alpha(
            primaryColor,
            0.08,
          )} 0%, ${alpha(warningColor, 0.04)} 100%)`,
          borderBottom: `1px solid ${alpha(dividerColor, 0.2)}`,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: textPrimary,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            fontSize: "1.1rem",
          }}
        >
          <SwitchAccountIcon sx={{ color: primaryColor, fontSize: "1.4rem" }} />
          Workspace
        </Typography>
      </Box>

      {/* Entities List */}
      <Box sx={{ padding: "8px 0" }}>
        <React.Fragment key={selectedEntity.id}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "14px 24px",
              cursor: "pointer",
              backgroundColor: alpha(primaryColor, 0.1),
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                backgroundColor: alpha(primaryColor, 0.15),
                transform: "translateX(4px)",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
              },
              position: "relative",
              borderRadius: "8px",
              margin: "0 8px 4px",
              border: `2px solid ${alpha(primaryColor, 0.3)}`,
            }}
          >
            {/* Entity Info */}
            <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              >
                <Avatar
                  sx={{
                    width: 42,
                    height: 42,
                    bgcolor: `linear-gradient(135deg, ${warningColor} 0%, ${alpha(
                      warningColor,
                      0.7,
                    )} 100%)`,
                    color: "white",
                    fontSize: "16px",
                    fontWeight: 700,
                    mr: 2.5,
                    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.05)",
                      boxShadow: "0 6px 12px rgba(0, 0, 0, 0.2)",
                    },
                  }}
                >
                  {selectedEntity.name.charAt(0).toUpperCase()}
                </Avatar>
              </Badge>
              <Box sx={{ flex: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 0.5,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 700,
                      color: textPrimary,
                      fontSize: "0.95rem",
                    }}
                  >
                    {selectedEntity.name || "Unnamed Workspace"}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          <Divider
            sx={{
              margin: "8px 32px",
              backgroundColor: alpha(dividerColor, 0.2),
            }}
          />
        </React.Fragment>
      </Box>
    </Paper>
  );
};

const Header: React.FC<HeaderProps> = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [openTimerPopup, setOpenTimerPopup] = useState(false);
  const [requestedEntityId, setRequestedEntityId] = useState<any>(null);
  const [switchingEntities, setSwitchingEntities] = useState(false);
  const [entityAvatarUrls, setEntityAvatarUrls] = useState<{
    [key: string]: string;
  }>({});
  const [hasFetchedAvatars, setHasFetchedAvatars] = useState(false);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const app = useSelector((state: any) => state.app?.allData || {});
  const timer = useSelector((state: any) => state.app?.timer || {});

  // Function to fetch entity avatar
  const fetchEntityAvatar = async (avatarFilename: string) => {
    if (!avatarFilename) return null;

    try {
      const response = await getRequest(
        `get_file/`,
        {
          folder: MediaFolders.ENTITY_IMAGES,
          filename: avatarFilename,
          as_attachment: false,
        },
        navigate,
        {},
        "blob",
      );
      if (response?.data) {
        const imageUrl = URL.createObjectURL(new Blob([response.data]));
        return imageUrl;
      }
    } catch (error) {
      console.error("Error fetching entity image:", error);
      dispatch(
        uiActions.updateNotification({
          message: "Failed to download entity image. Please try again later.",
          level: "error",
        }),
      );
    }
    return null;
  };

  // Fetch avatars for all entities
  const fetchAllEntityAvatars = async () => {
    if (!app.entities_info || !Array.isArray(app.entities_info)) {
      console.log("No entities info available yet");
      return;
    }

    console.log("Fetching avatars for entities:", app.entities_info);

    const avatarPromises = app.entities_info.map(async (entity: Entity) => {
      if (entity.entity_avatar) {
        console.log(
          `Fetching avatar for entity ${entity.id}:`,
          entity.entity_avatar,
        );
        const avatarUrl = await fetchEntityAvatar(entity.entity_avatar);
        if (avatarUrl) {
          return { entityId: entity.id, avatarUrl };
        }
      }
      return null;
    });

    const results = await Promise.all(avatarPromises);
    const newAvatarUrls: { [key: string]: string } = {};

    results.forEach((result) => {
      if (result) {
        newAvatarUrls[result.entityId] = result.avatarUrl;
      }
    });

    console.log("Fetched avatar URLs:", newAvatarUrls);
    setEntityAvatarUrls(newAvatarUrls);
    setHasFetchedAvatars(true);
  };

  // Fetch complete entity data including avatars when component mounts
  const fetchAllEntitiesData = async () => {
    if (!app.memberId) {
      console.log("No memberId available yet");
      return;
    }

    try {
      console.log("Fetching all entities data for member:", app.memberId);

      const response = await getRequest(`entities/`, {}, navigate);

      if (response?.data?.isOk && response.data.data) {
        const entitiesData = response.data.data;
        console.log("Fetched entities data:", entitiesData);

        const updatedAppData = {
          ...app,
          entities_info: entitiesData.map((entity: any) => ({
            ...entity,
            isSelected: entity.id === app.entityId,
            entity_avatar: entity.avatar || entity.entity_avatar,
          })),
        };

        dispatch(
          setAppData({
            data: updatedAppData,
          }),
        );

        await fetchAllEntityAvatars();
      }
    } catch (error) {
      console.error("Error fetching entities data:", error);
    }
  };

  useEffect(() => {
    console.log("Header mounted, checking for entities data...");

    const initializeAvatars = async () => {
      if (
        app.entities_info &&
        app.entities_info.length > 0 &&
        !hasFetchedAvatars
      ) {
        console.log("Entities info available, fetching avatars...");
        await fetchAllEntityAvatars();
      } else if (
        (!app.entities_info || app.entities_info.length === 0) &&
        app.memberId
      ) {
        console.log("No entities info, fetching complete entities data...");
        await fetchAllEntitiesData();
      }
    };

    initializeAvatars();
  }, [app.entities_info, app.memberId, hasFetchedAvatars]);

  useEffect(() => {
    if (app.entities_info && app.entities_info.length > 0) {
      console.log(
        "Entities info changed, checking if we need to fetch avatars...",
      );

      const entitiesWithAvatars = app.entities_info.filter(
        (entity: Entity) =>
          entity.entity_avatar && !entityAvatarUrls[entity.id],
      );

      if (entitiesWithAvatars.length > 0) {
        console.log("Found new entities with avatars, fetching...");
        fetchAllEntityAvatars();
      }
    }
  }, [app.entities_info]);

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      const response = await getRequest(
        `notification-list/${app.memberId}/`,
        {},
        navigate,
      );
      if (response?.data?.isOk) {
        setNotifications(response.data.data.notifications || []);
        setUnreadCount(response.data.data.unread_count || 0);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  useEffect(() => {
    if (app.memberId) {
      fetchNotifications();
    }
  }, [app.memberId]);

  useEffect(() => {
    const interval = setInterval(fetchNotifications, 180000);
    return () => clearInterval(interval);
  }, [app.memberId]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleMenuClose = () => {
    setIsMenuOpen(false);
  };

  const selectedEntity = app.entities_info?.find(
    (entity: Entity) => entity.id === app.entityId,
  );

  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
  };

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await postRequest(
        `notifications/${notificationId}/mark-as-read/`,
        {},
        navigate,
      );

      setNotifications((prevNotifications) =>
        prevNotifications.map((n: any) =>
          n.id === notificationId
            ? { ...n, is_read: true, read_at: new Date().toISOString() }
            : n,
        ),
      );

      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await postRequest(
        `notifications/mark-all-as-read/${app.memberId}/`,
        {},
        navigate,
      );

      setNotifications((prevNotifications) =>
        prevNotifications.map((n: any) => ({
          ...n,
          is_read: true,
          read_at: new Date().toISOString(),
        })),
      );

      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  const handleDeleteNotification = async (notificationId: string) => {
    try {
      const notificationToDelete = notifications.find(
        (n: any) => n.id === notificationId,
      );

      await deleteRequest(`notifications/${notificationId}/`, {}, navigate);

      setNotifications((prevNotifications) =>
        prevNotifications.filter((n: any) => n.id !== notificationId),
      );

      if (notificationToDelete && !notificationToDelete.is_read) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  const handleDeleteAllNotifications = async () => {
    try {
      await deleteRequest(
        `notifications/delete-by-member/${app.memberId}/`,
        {},
        navigate,
      );

      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error("Error deleting all notifications:", error);
    }
  };

  const calculateTimePassed = (createdAt: string) => {
    const createdDate = new Date(createdAt);
    const now = new Date();
    const diffInSeconds = Math.floor(
      (now.getTime() - createdDate.getTime()) / 1000,
    );

    if (diffInSeconds < 60) {
      return `${diffInSeconds}s ago`;
    } else if (diffInSeconds < 3600) {
      return `${Math.floor(diffInSeconds / 60)}m ago`;
    } else if (diffInSeconds < 86400) {
      return `${Math.floor(diffInSeconds / 3600)}h ago`;
    } else {
      return `${Math.floor(diffInSeconds / 86400)}d ago`;
    }
  };

  // Get the current entity's avatar URL
  const getCurrentEntityAvatar = () => {
    if (!selectedEntity) return undefined;

    if (selectedEntity.entity_avatar && entityAvatarUrls[selectedEntity.id]) {
      return entityAvatarUrls[selectedEntity.id];
    }

    if (selectedEntity.avatarUrl) {
      return selectedEntity.avatarUrl;
    }

    return undefined;
  };

  // Color palette for entity avatars
  const entityColorPalette = [
    "#7E57C2",
    "#5C6BC0",
    "#42A5F5",
    "#26C6DA",
    "#66BB6A",
    "#9CCC65",
    "#D4E157",
    "#FFCA28",
    "#FFA726",
    "#FF7043",
  ];
  const getEntityColor = (entityName?: string) => {
    if (!entityName) return "#7E57C2";
    const charCode = entityName.charCodeAt(0);
    return entityColorPalette[charCode % entityColorPalette.length];
  };

  return (
    <AppBar
      position="static"
      sx={{
        backgroundColor: "white",
        boxShadow: "0 2px 20px rgba(0, 0, 0, 0.08)",
        borderBottom: "1px solid #E2E8F0",
      }}
      className={styles["container"]}
    >
      <Toolbar sx={{ minHeight: "70px", padding: "0 24px !important" }}>
        <a href="../entities" style={{ textDecoration: "none" }}>
          <div className={styles["logo-container"]}>
            {!isSmallScreen ? (
              <>
                <img src={logoWithLine} alt="Logo" style={{ height: "32px" }} />
                <p
                  style={{
                    color: theme.palette.secondary.main,
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    marginLeft: "12px",
                    letterSpacing: "-0.5px",
                  }}
                >
                  TickTrackIT
                </p>
              </>
            ) : (
              <img src={logo} alt="Logo" style={{ height: "32px" }} />
            )}
          </div>
        </a>

        {/* Icons on the right */}
        <div
          className={styles.headerRight}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: "20px",
            marginLeft: "auto",
          }}
        >
          {/* Notifications Icon and Menu */}
          {!isSmallScreen && (
            <div style={{ position: "relative" }}>
              <Tooltip title="Notifications" arrow>
                <Box
                  onClick={toggleNotifications}
                  sx={{
                    position: "relative",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "44px",
                    height: "44px",
                    borderRadius: "12px",
                    backgroundColor: alpha("#7E57C2", 0.08),
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: alpha("#7E57C2", 0.15),
                      transform: "scale(1.05)",
                    },
                  }}
                >
                  <img
                    src={NotificationsIcon}
                    alt="Notifications"
                    width="22"
                    height="22"
                  />
                  {unreadCount > 0 && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: "-2px",
                        right: "-2px",
                        backgroundColor: "#FF7043",
                        color: "white",
                        borderRadius: "50%",
                        height: "20px",
                        width: "20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "11px",
                        fontWeight: 700,
                        boxShadow: "0 2px 8px rgba(255, 112, 67, 0.4)",
                        animation: "pulse 2s infinite",
                        "@keyframes pulse": {
                          "0%": { transform: "scale(1)" },
                          "50%": { transform: "scale(1.1)" },
                          "100%": { transform: "scale(1)" },
                        },
                      }}
                    >
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </Box>
                  )}
                </Box>
              </Tooltip>

              {/* Notification Menu */}
              {showNotifications && (
                <Paper
                  sx={{
                    position: "absolute",
                    top: "55px",
                    right: 0,
                    color: "#2D3748",
                    backgroundColor: "white",
                    borderRadius: "16px",
                    boxShadow:
                      "0 20px 60px rgba(0, 0, 0, 0.2), 0 5px 15px rgba(0, 0, 0, 0.1)",
                    zIndex: 1400,
                    width: "380px",
                    maxHeight: "500px",
                    overflow: "hidden",
                    border: `1px solid ${alpha("#E2E8F0", 0.3)}`,
                  }}
                >
                  <Box
                    sx={{
                      p: 2.5,
                      borderBottom: `1px solid ${alpha("#E2E8F0", 0.3)}`,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontSize: "1.1rem",
                          fontWeight: 700,
                          color: "#2D3748",
                        }}
                      >
                        Notifications
                      </Typography>
                      <Box sx={{ display: "flex", gap: "8px" }}>
                        {unreadCount > 0 && (
                          <Chip
                            label="Mark all read"
                            size="small"
                            onClick={handleMarkAllAsRead}
                            sx={{
                              backgroundColor: alpha("#7E57C2", 0.1),
                              color: "#7E57C2",
                              fontWeight: 600,
                              cursor: "pointer",
                              "&:hover": {
                                backgroundColor: alpha("#7E57C2", 0.2),
                              },
                            }}
                          />
                        )}
                        {notifications.length > 0 && (
                          <Chip
                            label="Clear all"
                            size="small"
                            onClick={handleDeleteAllNotifications}
                            sx={{
                              backgroundColor: alpha("#FF7043", 0.1),
                              color: "#FF7043",
                              fontWeight: 600,
                              cursor: "pointer",
                              "&:hover": {
                                backgroundColor: alpha("#FF7043", 0.2),
                              },
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ color: "#718096", fontSize: "0.8rem" }}
                    >
                      {notifications.length} notification
                      {notifications.length !== 1 ? "s" : ""}
                    </Typography>
                  </Box>

                  <Box sx={{ maxHeight: "350px", overflowY: "auto", p: 1 }}>
                    {notifications.map((notification: any) => (
                      <Paper
                        key={notification.id}
                        elevation={0}
                        sx={{
                          p: 2,
                          mb: 1.5,
                          borderRadius: "12px",
                          backgroundColor: notification.is_read
                            ? "#ffffff"
                            : alpha("#7E57C2", 0.05),
                          border: `1px solid ${
                            notification.is_read
                              ? alpha("#E2E8F0", 0.3)
                              : alpha("#7E57C2", 0.2)
                          }`,
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "translateX(4px)",
                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                          },
                        }}
                        onClick={() =>
                          !notification.is_read &&
                          handleMarkAsRead(notification.id)
                        }
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              width: "36px",
                              height: "36px",
                              borderRadius: "10px",
                              backgroundColor: notification.is_read
                                ? alpha("#718096", 0.1)
                                : alpha("#7E57C2", 0.15),
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <img
                              src={NotificationIcon}
                              alt="Notification"
                              width="18"
                              height="18"
                              style={{
                                opacity: notification.is_read ? 0.6 : 1,
                              }}
                            />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: notification.is_read ? 500 : 600,
                                color: notification.is_read
                                  ? "#718096"
                                  : "#2D3748",
                                lineHeight: 1.4,
                              }}
                            >
                              {notification.message}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                mt: 1,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ color: "#A0AEC0", fontSize: "0.75rem" }}
                              >
                                {calculateTimePassed(notification.created_at)}
                              </Typography>
                              {!notification.is_read && (
                                <Tooltip title="Mark as read" arrow>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleMarkAsRead(notification.id);
                                    }}
                                    sx={{
                                      padding: "4px",
                                      backgroundColor: alpha("#7E57C2", 0.1),
                                      "&:hover": {
                                        backgroundColor: alpha("#7E57C2", 0.2),
                                      },
                                    }}
                                  >
                                    <img
                                      src={NotificationIconRead}
                                      alt="Mark as read"
                                      width="16"
                                      height="16"
                                    />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </Paper>
                    ))}

                    {notifications.length === 0 && (
                      <Box sx={{ textAlign: "center", py: 6 }}>
                        <img
                          src={NotificationsIcon}
                          alt="No notifications"
                          width="48"
                          height="48"
                          style={{ opacity: 0.3, marginBottom: "16px" }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: "#A0AEC0", fontStyle: "italic" }}
                        >
                          No notifications yet
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
              )}
            </div>
          )}

          {/* Entity Avatar with Star */}
          <Tooltip title="Switch workspace" arrow>
            <Box
              sx={{ position: "relative", cursor: "pointer" }}
              onClick={toggleMenu}
            >
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                badgeContent={
                  <Box
                    sx={{
                      backgroundColor: "#FFB74D",
                      borderRadius: "50%",
                      width: "20px",
                      height: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 2px 8px rgba(255, 183, 77, 0.6)",
                      border: "2px solid white",
                      animation: "starPulse 2s infinite",
                      "@keyframes starPulse": {
                        "0%": { transform: "scale(1)" },
                        "50%": { transform: "scale(1.2)" },
                        "100%": { transform: "scale(1)" },
                      },
                    }}
                  >
                    <StarIcon sx={{ fontSize: "12px", color: "white" }} />
                  </Box>
                }
              >
                <Avatar
                  src={getCurrentEntityAvatar()}
                  sx={{
                    background: `linear-gradient(135deg, ${getEntityColor(
                      selectedEntity?.name,
                    )} 0%, ${alpha(
                      getEntityColor(selectedEntity?.name),
                      0.7,
                    )} 100%)`,
                    color: "white",
                    cursor: "pointer",
                    width: 44,
                    height: 44,
                    fontSize: "18px",
                    fontWeight: 700,
                    border: `3px solid #FFB74D`,
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.05)",
                      boxShadow: "0 6px 20px rgba(0, 0, 0, 0.2)",
                    },
                  }}
                >
                  {!getCurrentEntityAvatar() &&
                    (selectedEntity?.name
                      ? selectedEntity.name.charAt(0).toUpperCase()
                      : "U")}
                </Avatar>
              </Badge>
              {isMenuOpen && (
                <ExpandMoreIcon
                  sx={{
                    position: "absolute",
                    bottom: "-8px",
                    left: "50%",
                    transform: "translateX(-50%) rotate(180deg)",
                    color: "#7E57C2",
                    fontSize: "16px",
                    backgroundColor: "white",
                    borderRadius: "50%",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  }}
                />
              )}
            </Box>
          </Tooltip>
        </div>

        {/* Entity Menu */}
        <EntityMenu isOpen={isMenuOpen} />
      </Toolbar>
    </AppBar>
  );
};

export default Header;
